import React, { useState, MouseEvent } from 'react';
import { I } from '../icons-ds';
import { Button, ButtonSize } from './Button';

export type CopyButtonProps = {
  text: string;
  label?: string;
  size?: ButtonSize;
  onCopied?: () => void;
};

/**
 * Plain-text copy only. Uses navigator.clipboard.writeText (no rich-text /
 * setData('text/html')), with a hidden-textarea execCommand fallback for
 * older Safari. This guarantees pastes land as clean text in any EMR.
 */
export async function copyPlainText(text: string): Promise<boolean> {
  const plain = String(text);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(plain);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = plain;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
}

export function CopyButton({ text, label = 'Copy', size = 'sm', onCopied }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const copy = async (e?: MouseEvent) => {
    e?.stopPropagation();
    const ok = await copyPlainText(text);
    if (!ok) return;
    setCopied(true);
    onCopied?.();
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Button
      size={size}
      variant={copied ? 'primary' : 'secondary'}
      icon={copied ? <I.check size={14} stroke={2.6} /> : <I.copy size={14} />}
      onClick={copy}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

export default CopyButton;
